import React from 'react';
import { Button, Card, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcService } from '@/services/epc.service';

export interface AwpPanelProps {
  projectId?: string;
}

export const AwpPanel: React.FC<AwpPanelProps> = ({ projectId }) => {
  const qc = useQueryClient();

  const pathsQ = useQuery({
    queryKey: ['awp-paths', projectId],
    queryFn: () => epcService.getAwpPaths(projectId!),
    enabled: !!projectId,
  });

  const releasesQ = useQuery({
    queryKey: ['awp-releases', projectId],
    queryFn: () => epcService.getAwpReleases(projectId!),
    enabled: !!projectId,
  });

  const convertM = useMutation({
    mutationFn: () => epcService.convertAwpFromContract(projectId!),
    onSuccess: (data: any) => {
      message.success(
        `Converted: ${data?.projectWbsCreated ?? 0} WBS, ${data?.areasCreated ?? 0} areas, ${data?.packagesCreated ?? 0} packages`
      );
      void qc.invalidateQueries({ queryKey: ['awp-paths'] });
      void qc.invalidateQueries({ queryKey: ['awp-releases'] });
      void qc.invalidateQueries({ queryKey: ['epc-wbs-n'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  if (!projectId) {
    return <Card size="small">Select a project to view AWP (Path of Construction).</Card>;
  }

  const paths = (pathsQ.data as any[]) || [];
  const releases = (releasesQ.data as any[]) || [];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Card
        size="small"
        title="Path of Construction"
        extra={
          <Button size="small" loading={convertM.isPending} onClick={() => convertM.mutate()}>
            Run contract conversion
          </Button>
        }
      >
        <Table
          size="small"
          rowKey="id"
          loading={pathsQ.isLoading}
          dataSource={paths}
          pagination={false}
          columns={[
            { title: 'Name', dataIndex: 'name' },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 100,
              render: (v) => <Tag>{v}</Tag>,
            },
            { title: 'Baseline', dataIndex: 'baseline_revision', width: 80 },
          ]}
        />
      </Card>

      <Card size="small" title="Package release board">
        <Table
          size="small"
          rowKey="id"
          loading={releasesQ.isLoading}
          dataSource={releases}
          pagination={false}
          columns={[
            { title: 'Package', dataIndex: 'package_number', width: 120 },
            { title: 'Description', dataIndex: 'package_description', ellipsis: true },
            {
              title: 'Release',
              dataIndex: 'release_status',
              width: 100,
              render: (v) => (
                <Tag color={v === 'Released' ? 'green' : v === 'Blocked' ? 'red' : 'default'}>
                  {v}
                </Tag>
              ),
            },
            {
              title: 'Readiness',
              dataIndex: 'readiness_score',
              width: 90,
              render: (v) => (v != null ? `${v}%` : '—'),
            },
          ]}
        />
      </Card>
    </Space>
  );
};
